import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentDate,
  getCurrentTime,
  getDateOfLast,
} from "../utils/getCurrentDate";
import { formatAmount } from "../utils/commonUtils";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import ReUsableModal from "../components/ReUsableModal";
import { toast } from "react-toastify";
import { Pencil, CheckLg, XLg } from "react-bootstrap-icons";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { setCredentials } from "../slices/authSlice";
import Loader from "../components/Loader";
import FieldWarning from "../components/FieldWarning";
import Message from "../components/Message";
import {
  useCreateExpenseMutation,
  useGetExpensesQuery,
  useUpdateExpenseMutation,
} from "../slices/expenseSlice";
import {
  FaSort,
  FaRupeeSign,
  FaArrowDown,
  FaArrowUp,
  FaWallet,
  FaFileExcel,
  FaFilePdf,
  FaPlus,
  FaListUl,
} from "react-icons/fa";
import { useGetOrdersQuery } from "../slices/OrdersSlice";

const cardStyle = {
  backgroundColor: "#ffffff",
  border: "1px solid #e3ecf3",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(29, 95, 145, 0.08)",
};

const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: "600",
  color: "#344a5e",
  marginBottom: "0.3rem",
};

const summaryChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0.45rem 0.9rem",
  borderRadius: "14px",
  border: "1px solid #b7dbf7",
  background:
    "linear-gradient(to bottom right, #ffffff 0%, #d9ebf9 45%, #c2e5fc 100%)",
  color: "#1d5f91",
  fontWeight: "600",
  fontSize: "0.85rem",
};

const headStyle = {
  backgroundColor: "#f4f9fd",
  color: "#1d5f91",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  borderBottom: "2px solid #e3ecf3",
  padding: "0.6rem 0.75rem",
  whiteSpace: "nowrap",
};

const cellStyle = {
  padding: "0.55rem 0.75rem",
  verticalAlign: "middle",
  fontSize: "0.88rem",
};

const AccountsScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const [editEnabled, setEditEnabled] = useState("");
  const [expenseAmount, setExpenseAmount] = useState();
  const [expenseType, setExpenseType] = useState("Select Expense Type");
  const [paidDate, setPaidDate] = useState(getCurrentDate());
  const [transactionId, setTransactionId] = useState("");
  const [notes, setNotes] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const [expenseAmountPresent, setExpenseAmountPresent] = useState();
  const [expenseTypePresent, setExpenseTypePresent] = useState();
  const [paidDatePresent, setPaidDatePresent] = useState(getCurrentDate());
  const [transactionIdPresent, setTransactionIdPresent] = useState("");
  const [notesPresent, setNotesPresent] = useState("");

  const [addModalShow, setAddModalShow] = useState(false);
  const [modalShow, setModalShow] = useState(false);

  const [filterValue, setFilterValue] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [startDate, setStartDate] = useState(getDateOfLast(30));
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [totalIncome, setTotalIncome] = useState(0);

  const [getApiQuery, setGetApiQuery] = useState(
    `paidDate[gte]=${startDate}&paidDate[lte]=${endDate}${filterValue}&sort=-paidDate`
  );

  const [apiQuery, setApiQuery] = useState(
    `createdAt[gte]=${getCurrentDate()}&sort=-createdAt`
  );

  const [todaysExpenseCount, setTodaysExpenseCount] = useState("");
  const [totalExpense, setTotalExpense] = useState();
  const [expensesToList, setExpensesToList] = useState([]);

  const dispatch = useDispatch();

  const expenseTypes = [
    "Select Expense Type",
    "Food Ingredients",
    "Salary",
    "Water",
    "Electricity",
    "Vegetables",
    "Fruits",
    "Dairy Products",
    "Meat",
    "Fish",
    "Cleaning Supplies",
    "Stationery",
    "Office Supplies",
    "Rent",
    "Carpenter Work",
    "Plumbing Work",
    "Maintenance",
    "Equipment",
    "Others",
  ];
  const filterOptions = [
    "&expenseType=All",
    "&expenseType=Food Ingredients",
    "&expenseType=Salary",
    "&expenseType=Water",
    "&expenseType=Electricity",
    "&expenseType=Vegetables",
    "&expenseType=Fruits",
    "&expenseType=Dairy Products",
    "&expenseType=Meat",
    "&expenseType=Fish",
    "&expenseType=Cleaning Supplies",
    "&expenseType=Stationery",
    "&expenseType=Office Products",
    "&expenseType=Carpenter Work",
    "&expenseType=Plumbing Work",
    "&expenseType=Maintenance",
    "&expenseType=Equipment",
    "&expenseType=Rent",
    "&expenseType=Others",
  ];

  let {
    data: expenses,
    isLoading,
    error,
    refetch,
  } = useGetExpensesQuery({
    token: userInfo ? userInfo.token : "Bearer a",
    query: getApiQuery,
  });

  const {
    data: orders,
    isLoading: orderLoading,
    error: orderError,
    refetch: orderRefetch,
  } = useGetOrdersQuery({ query: apiQuery, token: userInfo?.token });

  const [createExpense, { isLoading: createLoading }] =
    useCreateExpenseMutation();
  const [updateExpense] = useUpdateExpenseMutation();

  useEffect(() => {
    if (paidDate <= getCurrentDate()) {
      setPaidDatePresent(false);
    }

    if (expenseType !== undefined && expenseType !== "Select Expense Type") {
      setExpenseTypePresent(false);
    }

    if (notes?.length < 1000) {
      setNotesPresent(false);
    }
    if (expenseAmount !== undefined && expenseAmount >= 1) {
      setExpenseAmountPresent(false);
    }

    if (transactionId?.length < 40) {
      setTransactionIdPresent(false);
    }
  }, [paidDate, expenseAmount, expenseType, transactionId, notes]);

  const verifyInputFeilds = async () => {
    let fieldValidationSuccess = true;
    if (paidDate === undefined || paidDate?.split("T")[0] > getCurrentDate()) {
      setPaidDatePresent(true);
      fieldValidationSuccess = false;
    }

    if (expenseType === undefined || expenseType === "Select Expense Type") {
      setExpenseTypePresent(true);
      fieldValidationSuccess = false;
    }

    if (!expenseAmount || expenseAmount === undefined || expenseAmount < 1) {
      setExpenseAmountPresent(true);
      fieldValidationSuccess = false;
    }

    if (transactionId?.length > 40) {
      setTransactionIdPresent(true);
      fieldValidationSuccess = false;
    }

    if (notes?.length > 1000) {
      setNotesPresent(true);
      fieldValidationSuccess = false;
    }

    if (!fieldValidationSuccess)
      toast.error(
        "Some of the fields have invalid inputs. Please provide valid details"
      );
    return fieldValidationSuccess;
  };

  const handleAddExpense = async () => {
    try {
      const expenseData = {
        expenseAmount: Number(expenseAmount),
        expenseType,
        paidDate,
        transactionId,
        notes,
      };

      const res = await createExpense({
        token: userInfo.token,
        payload: expenseData,
      }).unwrap();

      toast.success("Expense Added Successfully");

      setPaidDate(getCurrentDate());
      setExpenseAmount("");
      setExpenseType("Select Expense Type");
      setTransactionId("");
      setNotes("");
      setAddModalShow(false);
      setShowAdd(false);
      refetch();
    } catch (err) {
      console.log(err);

      toast.error(
        err?.data?.message || err?.data?.error || "Failed to create expense"
      );
    }
  };
  const submitEdit = async () => {
    try {
      const updatedExpense = {
        expenseAmount: Number(expenseAmount),
        expenseType,
        paidDate,
        transactionId,
        notes,
      };

      const res = await updateExpense({
        _id: editEnabled,
        token: userInfo.token,
        payload: updatedExpense,
      }).unwrap();
      toast.success("Expense Updated Successfully");

      setPaidDate(getCurrentDate());
      setExpenseAmount("");
      setExpenseType("Select Expense Type");
      setTransactionId("");
      setNotes("");
      setEditEnabled("");
      setModalShow(false);
      refetch();
    } catch (err) {
      console.log(err);
      toast.error(
        err?.data?.message || err?.data?.error || "Failed to update expense"
      );
    }
  };

  const hanbdleSubmit = async (user) => {
    if (await verifyInputFeilds()) setModalShow(true);
  };

  const hanbdleAddExpense = async (user) => {
    if (await verifyInputFeilds()) setAddModalShow(true);
  };

  const handleGetExpense = async () => {
    if (startDate > getCurrentDate())
      toast.error("Start date can not be greater than today");
    else if (startDate > endDate)
      toast.error("Start date can not be greater than End date");
    else {
      if (endDate?.split("T")[0] > getCurrentDate())
        setEndDate(getCurrentDate() + getCurrentTime());
      setGetApiQuery(
        `paidDate[gte]=${startDate}&paidDate[lte]=${endDate}${filterValue}&sort=-paidDate`
      );
      setApiQuery(
        `createdAt[gte]=${startDate}&createdAt[lte]=${endDate}&sort=-createdAt`
      );
      dispatch(
        setCredentials({
          ...userInfo,
          accountsStartDate: startDate,
          accountsEndDate: endDate,
        })
      );
    }
  };

  useEffect(() => {
    refetch();
    orderRefetch();
  }, [refetch, editEnabled]);

  useEffect(() => {
    const todaysExpenseCount = isLoading
      ? 0
      : error
      ? 0
      : expenses
      ? expenses?.length
      : 0;

    let expenseAmount = 0;
    const aa = isLoading
      ? 0
      : error
      ? 0
      : expenses?.map((e) => {
          expenseAmount = expenseAmount + e.expenseAmount;
          return 0;
        });

    const exp = isLoading ? [] : error ? [] : expenses ? expenses : [];
    const sortedExpenses = [...exp]?.sort((a, b) => {
      if (
        a[sortConfig.key]?.toString().toLowerCase() <
        b[sortConfig.key]?.toString().toLowerCase()
      ) {
        return sortConfig?.direction === "asc" ? -1 : 1;
      }
      if (
        a[sortConfig.key]?.toString().toLowerCase() >
        b[sortConfig.key]?.toString().toLowerCase()
      ) {
        return sortConfig?.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setExpensesToList(sortedExpenses);
    setTotalExpense(expenseAmount);
    setTodaysExpenseCount(todaysExpenseCount);
    let totalIncome = 0;

    orders?.forEach((order) => {
      totalIncome += Number(order.grossTotal || 0);
    });
    setTotalIncome(totalIncome);
  }, [setTodaysExpenseCount, expenses, isLoading, error, orders, sortConfig]);

  const handleSelect = (e) => {
    setExpenseType(e.target.value);
  };

  const handleStatusFilter = (val) => {
    if (val.includes("All")) setFilterValue("");
    else setFilterValue(val);
  };

  const hanbdleCancelEdit = () => {
    setEditEnabled(false);

    setPaidDate(getCurrentDate());
    setExpenseAmount("");
    setExpenseType("Select Expense Type");
    setTransactionId("");
    setNotes("");
  };

  useEffect(() => {
    if (error?.data?.error) {
      setTodaysExpenseCount(0);
      setTotalExpense(0);
    }
  }, [error]);

  const exportToExcel = () => {
    if (error) {
      toast.error(
        "No Expenses available to Download for the selected Date range"
      );
    } else {
      const formattedData = expenses?.map((exp) => ({
        "Expense Type": exp.expenseType,
        "Expense Amount": Number(formatAmount(exp.expenseAmount)),
        "Paid Date": exp.paidDate
          ? exp.paidDate.split("T")[0]
          : exp.lastPaidDate.split("T")[0],
        "Transaction ID": exp.transactionId,
        Notes: exp.notes,
        "Updated By": exp.updatedBy,
      }));

      const totalPaidAmount = expenses?.reduce(
        (total, user) => total + Number(user.expenseAmount),
        0
      );

      formattedData.push({
        "Expense Type": "Total",
        "Expense Amount": Number(formatAmount(totalPaidAmount)),
        "Paid Date": "",
        "Transaction ID": "",
        Notes: "",
        "Updated By": "",
      });

      const workSheet = XLSX.utils.json_to_sheet(formattedData);
      const workBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workBook, workSheet, "Expenses");
      XLSX.writeFile(
        workBook,
        "Expenses_from " + startDate + " to " + endDate + ".xlsx"
      );
    }
  };

  // Function to export data to PDF
  const exportToPDF = () => {
    if (error) {
      toast.error(
        "No Expenses available to Download for the selected Date range"
      );
    } else {
      const doc = new jsPDF();
      doc.text("Expenses from " + startDate + " to " + endDate, 20, 10);
      doc.autoTable({
        startY: 20,
        head: [
          [
            "Expense Type",
            "Expense Amount",
            "Paid Date",
            "Transaction ID",
            "Notes",
            "Updated By",
          ],
        ],
        body: [
          ...expenses?.map((exp) => [
            exp.expenseType,
            formatAmount(exp.expenseAmount),
            exp.paidDate
              ? exp.paidDate.split("T")[0]
              : exp.lastPaidDate.split("T")[0],
            exp.transactionId,
            exp.notes,
            exp.updatedBy,
          ]),
          [
            { content: "Total", styles: { fontStyle: "bold" } },
            {
              content: formatAmount(
                expenses?.reduce(
                  (total, user) => total + Number(user.expenseAmount),
                  0
                )
              ),
              styles: { fontStyle: "bold" },
            },
            "",
            "",
            "",
          ],
        ],
      });
      doc.save("Expenses_from " + startDate + " to " + endDate + ".pdf");
    }
  };

  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const profit = Number(totalIncome) - Number(totalExpense || 0);

  return (
    <div style={{ padding: "0.5rem 0.6rem" }}>
      <ReUsableModal
        title="Edit this Expense?"
        payload={[]}
        show={modalShow}
        size="sm"
        onHide={() => setModalShow(false)}
        onSubmit={submitEdit}
        setEdit={setEditEnabled}
      />
      <ReUsableModal
        title="Add this Expense ?"
        show={addModalShow}
        payload={[]}
        size="sm"
        onHide={() => setAddModalShow(false)}
        onSubmit={handleAddExpense}
      />

      {/* Header / filter card */}
      <div
        style={{ ...cardStyle, padding: "0.9rem 1rem", marginBottom: "0.8rem" }}
      >
        <Row className="align-items-end" style={{ rowGap: "0.6rem" }}>
          <Col xs={12} lg={3}>
            <h5 className="mb-1">
              <strong style={{ color: "#1d5f91" }}>Accounts & Finance</strong>
            </h5>
            <small className="text-muted">
              Track income, expenses and profit
            </small>
          </Col>
          <Col xs={6} sm={3} lg={2}>
            <Form.Label
              className="mb-1"
              style={{ fontSize: "0.75rem", color: "#6c757d" }}
            >
              FROM
            </Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
          <Col xs={6} sm={3} lg={2}>
            <Form.Label
              className="mb-1"
              style={{ fontSize: "0.75rem", color: "#6c757d" }}
            >
              TO
            </Form.Label>
            <Form.Control
              type="date"
              value={endDate?.split("T")[0]}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
          <Col xs={6} sm={3} lg={2}>
            <Form.Label
              className="mb-1"
              style={{ fontSize: "0.75rem", color: "#6c757d" }}
            >
              TYPE
            </Form.Label>
            <Form.Select
              id="filterBy"
              value={filterValue}
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              {filterOptions?.map((e) => (
                <option key={e} value={e}>
                  {e?.split("=")[1]}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} sm={3} lg={1} className="d-grid">
            <Button
              className={`${userInfo.themeColor}ThemeHeaderTop`}
              style={{ borderRadius: "8px", fontWeight: "600" }}
              onClick={handleGetExpense}
            >
              GET
            </Button>
          </Col>
          <Col xs={12} sm={6} lg={2} className="d-grid">
            <Button
              variant={showAdd ? "outline-secondary" : "success"}
              style={{ borderRadius: "8px", fontWeight: "600" }}
              onClick={() => setShowAdd(!showAdd)}
            >
              {showAdd ? (
                <>
                  <FaListUl style={{ marginBottom: "2px" }} /> Show Expenses
                </>
              ) : (
                <>
                  <FaPlus style={{ marginBottom: "2px" }} /> Add Expense
                </>
              )}
            </Button>
          </Col>
        </Row>
      </div>

      {!showAdd ? (
        <>
          {/* Summary chips + downloads */}
          <div
            className="d-flex flex-wrap align-items-center"
            style={{ gap: "0.6rem", marginBottom: "0.8rem" }}
          >
            <span style={summaryChipStyle}>
              <FaArrowDown size={13} style={{ color: "#1e8e4e" }} />
              Income : ₹ {formatAmount(totalIncome)}
            </span>
            <span style={summaryChipStyle}>
              <FaArrowUp size={13} style={{ color: "#c0392b" }} />
              Expense : ₹ {formatAmount(totalExpense)}
            </span>
            <span
              style={{
                ...summaryChipStyle,
                color: profit >= 0 ? "#1e8e4e" : "#c0392b",
              }}
            >
              <FaWallet size={13} />
              Profit : ₹ {formatAmount(profit)}
            </span>
            <span style={summaryChipStyle}>
              <FaRupeeSign size={13} />
              Entries : {todaysExpenseCount || 0}
            </span>
            {!userInfo?.isMobileScreen && (
              <span
                className="d-flex"
                style={{ gap: "0.5rem", marginLeft: "auto" }}
              >
                <Button
                  variant="outline-success"
                  size="sm"
                  style={{ borderRadius: "8px" }}
                  onClick={exportToExcel}
                >
                  <FaFileExcel style={{ marginBottom: "2px" }} /> Excel
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={{ borderRadius: "8px" }}
                  onClick={exportToPDF}
                >
                  <FaFilePdf style={{ marginBottom: "2px" }} /> PDF
                </Button>
              </span>
            )}
          </div>

          {isLoading ? (
            <Loader />
          ) : error ? (
            <Message>{error?.data?.error}</Message>
          ) : expensesToList?.length === 0 ? (
            <div
              style={{
                ...cardStyle,
                padding: "1.5rem",
                textAlign: "center",
                color: "#6c757d",
              }}
            >
              No expenses found for the selected date range
            </div>
          ) : (
            <div style={{ ...cardStyle, overflow: "hidden" }}>
              <Table hover responsive className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th
                      style={{ ...headStyle, cursor: "pointer" }}
                      onClick={() => handleSort("expenseType")}
                    >
                      Expense Type <FaSort />
                    </th>
                    <th style={{ ...headStyle, textAlign: "right" }}>
                      Amount
                    </th>
                    <th
                      style={{ ...headStyle, cursor: "pointer" }}
                      onClick={() => handleSort("paidDate")}
                    >
                      Date <FaSort />
                    </th>
                    <th style={headStyle}>Transaction ID</th>
                    <th style={headStyle}>Updated By</th>
                    <th style={headStyle}>Notes</th>
                    <th style={{ ...headStyle, textAlign: "center" }}>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {expensesToList?.map((exp) =>
                    editEnabled === exp?._id ? (
                      <tr key={exp._id}>
                        <td style={cellStyle}>
                          <Form.Select
                            value={expenseType || exp.expenseType}
                            onChange={(e) => handleSelect(e)}
                          >
                            {expenseTypes?.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </Form.Select>
                          {expenseTypePresent && (
                            <FieldWarning text="Please select valid type" />
                          )}
                        </td>
                        <td style={cellStyle}>
                          <Form.Control
                            value={expenseAmount}
                            type="text"
                            onChange={(e) => setExpenseAmount(e.target.value)}
                          />
                          {expenseAmountPresent && (
                            <FieldWarning text="Please provide expense amount" />
                          )}
                        </td>
                        <td style={cellStyle}>
                          <Form.Control
                            value={paidDate?.split("T")[0]}
                            type="date"
                            onChange={(e) => setPaidDate(e.target.value)}
                          />
                          {paidDatePresent && (
                            <FieldWarning text="Please provide valid date" />
                          )}
                        </td>
                        <td style={cellStyle}>
                          <Form.Control
                            type="text"
                            value={transactionId}
                            onChange={(e) => setTransactionId(e.target.value)}
                          />
                          {transactionIdPresent && (
                            <FieldWarning text="Transaction Id should not exceed 40 chars" />
                          )}
                        </td>
                        <td style={cellStyle}>{exp.updatedBy}</td>
                        <td style={cellStyle}>
                          <Form.Control
                            value={notes}
                            type="text"
                            onChange={(e) => setNotes(e.target.value)}
                          />
                          {notesPresent && (
                            <FieldWarning text="Notes should not have more than 1000 chars" />
                          )}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Button
                            variant="outline-success"
                            size="sm"
                            style={{ borderRadius: "8px" }}
                            onClick={hanbdleSubmit}
                            title="Save"
                          >
                            <CheckLg />
                          </Button>{" "}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ borderRadius: "8px" }}
                            onClick={hanbdleCancelEdit}
                            title="Cancel"
                          >
                            <XLg />
                          </Button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={exp._id}>
                        <td style={{ ...cellStyle, fontWeight: "600" }}>
                          {exp.expenseType}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            textAlign: "right",
                            fontWeight: "600",
                            color: "#c0392b",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ₹ {formatAmount(exp.expenseAmount)}
                        </td>
                        <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                          {exp.paidDate ? exp.paidDate?.split("T")[0] : ""}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            color: "#6c757d",
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={exp.transactionId}
                        >
                          {exp.transactionId || "-"}
                        </td>
                        <td style={cellStyle}>{exp.updatedBy}</td>
                        <td
                          style={{
                            ...cellStyle,
                            color: "#6c757d",
                            maxWidth: "220px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={exp.notes}
                        >
                          {exp.notes || "-"}
                        </td>
                        <td style={{ ...cellStyle, textAlign: "center" }}>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ borderRadius: "8px" }}
                            onClick={() => {
                              setExpenseAmount(exp.expenseAmount);
                              setEditEnabled(exp?._id);
                              setNotes(exp.notes);
                              setTransactionId(exp.transactionId);
                              setPaidDate(exp.paidDate);
                              setExpenseType(exp.expenseType);
                            }}
                          >
                            <Pencil />
                          </Button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </>
      ) : (
        /* Add expense form */
        <div
          style={{
            ...cardStyle,
            padding: "1rem 1.1rem",
            maxWidth: "760px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              paddingBottom: "0.7rem",
              marginBottom: "0.9rem",
              borderBottom: "1px solid #eef4f9",
            }}
          >
            <div
              style={{ fontWeight: "700", color: "#1d5f91", fontSize: "0.95rem" }}
            >
              Add Expense
            </div>
            <small className="text-muted" style={{ fontSize: "0.75rem" }}>
              Record a new expense for this outlet
            </small>
          </div>
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Expense Type <span style={{ color: "#dc3545" }}>*</span>
              </Form.Label>
              <Form.Select value={expenseType} onChange={(e) => handleSelect(e)}>
                {expenseTypes?.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Form.Select>
              {expenseTypePresent && (
                <FieldWarning text="Please select valid type" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Expense Amount <span style={{ color: "#dc3545" }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={expenseAmount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setExpenseAmount(value);
                }}
                placeholder="Enter amount"
              />
              {expenseAmountPresent && (
                <FieldWarning text="Amount should be atleast 1 Rs" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Expense Date</Form.Label>
              <Form.Control
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
              />
              {paidDatePresent && (
                <FieldWarning text="Please provide valid date" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Transaction ID</Form.Label>
              <Form.Control
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Transaction / reference id"
              />
              {transactionIdPresent && (
                <FieldWarning text="Transaction id should not exceed 40 chars" />
              )}
            </Col>
            <Col xs={12}>
              <Form.Label style={labelStyle}>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes to remember"
              />
              {notesPresent && (
                <FieldWarning text="Notes should not have more than 1000 chars" />
              )}
            </Col>
            <Col xs={12} sm={6} className="d-grid mx-auto">
              <Button
                className={`${userInfo.themeColor}ThemeHeaderTop`}
                style={{
                  borderRadius: "8px",
                  fontWeight: "600",
                  padding: "0.6rem",
                }}
                onClick={hanbdleAddExpense}
                disabled={createLoading}
              >
                {createLoading ? "Adding..." : "Add Expense"}
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default AccountsScreen;
